import type { VocabularyAi } from "../application/ports/ai.ts";
import type { DiscoverCacheStore } from "../application/ports/discoverCache.ts";
import type { Platform } from "../application/ports/platform.ts";
import type { CredentialStore, SettingsStore } from "../application/ports/settings.ts";
import type { VocabularyStore } from "../application/ports/storage.ts";
import { VocabularyQueries } from "../application/vocabulary/queries.ts";
import { VocabularyCommands } from "../application/vocabulary/commands.ts";
import { GenerationController } from "../application/generation/controller.ts";
import { PracticeController } from "../application/practice/controller.ts";
import { ImportController } from "../application/import/controller.ts";
import { SettingsController } from "../application/settings/controller.ts";
import { PapaCsvParser } from "../infrastructure/csv/papa.ts";
import { FakeVocabularyAi } from "../infrastructure/ai/fake/FakeVocabularyAi.ts";
import { HttpVocabularyAi } from "../infrastructure/ai/http/HttpVocabularyAi.ts";
import { LangChainVocabularyAi } from "../infrastructure/ai/langchain/LangChainVocabularyAi.ts";
import { LocalStorageVocabularyStore } from "../infrastructure/storage/local-storage/adapter.ts";
import { LocalStorageDiscoverCache } from "../infrastructure/storage/local-storage/discoverCache.ts";
import { LocalCredentialStore, LocalSettingsStore } from "../infrastructure/storage/local-storage/settings.ts";
import { MemoryVocabularyStore } from "../infrastructure/storage/memory/adapter.ts";
import { createBrowserPlatform } from "../infrastructure/browser/platform.ts";
import type { CsvParser } from "../application/ports/csv.ts";
import type { AppServices } from "../application/appServices.ts";

class SwitchingVocabularyAi implements VocabularyAi {
  constructor(private readonly resolve: () => Promise<VocabularyAi>) {}

  async discoverMeanings(...args: Parameters<VocabularyAi["discoverMeanings"]>) {
    return (await this.resolve()).discoverMeanings(...args);
  }

  async generateMeaningPackages(...args: Parameters<VocabularyAi["generateMeaningPackages"]>) {
    return (await this.resolve()).generateMeaningPackages(...args);
  }
}

export interface CreateAppOptions {
  store?: VocabularyStore;
  ai?: VocabularyAi;
  platform?: Platform;
  credentials?: CredentialStore;
  settingsStore?: SettingsStore;
  discoverCache?: DiscoverCacheStore;
  csv?: CsvParser;
}

export async function createAppServices(options: CreateAppOptions = {}): Promise<AppServices> {
  const platform = options.platform ?? createBrowserPlatform();
  const store = options.store ?? new LocalStorageVocabularyStore();
  const credentials = options.credentials ?? new LocalCredentialStore();
  const settingsStore = options.settingsStore ?? new LocalSettingsStore();
  const discoverCache = options.discoverCache ?? new LocalStorageDiscoverCache();
  const csv = options.csv ?? new PapaCsvParser();
  const gemini = new LangChainVocabularyAi(credentials);
  const sample = new FakeVocabularyAi();
  const ai =
    options.ai ??
    new SwitchingVocabularyAi(async () => {
      const settings = await settingsStore.get();
      if (settings.aiMode === "sample") {
        return sample;
      }
      if (settings.aiMode === "http") {
        return new HttpVocabularyAi(settings.httpBaseUrl || "http://127.0.0.1:8787");
      }
      return gemini;
    });
  const load = await store.load();
  const queries = new VocabularyQueries(store);
  const commands = new VocabularyCommands(store, platform.clock, platform.ids);
  const generation = new GenerationController(store, ai, platform, commands, discoverCache);
  const practice = new PracticeController(store, platform);
  const importer = new ImportController(csv, ai, store, platform, commands);
  const settingsController = new SettingsController(
    settingsStore,
    credentials,
    platform.network,
    (apiKey, signal) => gemini.testKey(apiKey, signal),
    discoverCache,
  );
  await settingsController.hydrate();
  return {
    store,
    queries,
    commands,
    generation,
    practice,
    importer,
    settingsController,
    platform,
    credentials,
    settingsStore,
    discoverCache,
    ai,
    load,
  };
}

export { MemoryVocabularyStore };
